import { useState } from "react";
import { Keyboard } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { gql, useMutation } from "@apollo/client";

import { Reviewer } from "../screens/types";
import Button from "./Button";
import { useErrorScroll } from "./ErrorMessage";
import { MemberPicker } from "./MemberPickerInput";
import useSnackBar from "../hooks/useSnackBar";
import { MY_ACTIVITY_DETAILS_FRAGMENT } from "../screens/fragments";
import ActionsContainer from "./ActionsContainer";

type FormData = {
  memberToReview?: Reviewer;
};

const defaultValues: FormData = {
  memberToReview: undefined,
};

export const MUTATION = gql`
  mutation reassignDeferredCompReviewer($input: DeferredCompReassignReviewerInput!) {
    reassignDeferredCompReviewer(input: $input) {
      message
      success
      submission {
        ...MyActivityDetailsFragment
      }
    }
  }
  ${MY_ACTIVITY_DETAILS_FRAGMENT}
`;

interface Props {
  onSuccess(): void;
  onDismissModal(): void;
  visible: boolean;
  pointsRequest: { id: string };
}

export default function UpdateReviewer({ pointsRequest: { id }, visible, onSuccess, onDismissModal }: Props) {
  const [sendMutation, { loading }] = useMutation(MUTATION);

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<FormData>({ defaultValues, mode: "onChange" });
  const [apiError, setApiError] = useState("");
  const { onSubmitError } = useErrorScroll({ defaultValues });
  const showSnackBar = useSnackBar();

  async function onSubmit({ memberToReview }: FormData) {
    try {
      const {
        data: {
          reassignDeferredCompReviewer: { success, message },
        },
      } = await sendMutation({
        variables: {
          input: {
            reviewerId: memberToReview?.id,
            submissionId: id,
          },
        },
      });
      if (success) {
        showSnackBar({ message: "Activity Reviewer Updated", duration: 3000 });
        onDismissModal();
        onSuccess();
      } else {
        throw new Error(message);
      }
    } catch (e: any) {
      setApiError((e as Error).message || "Something went wrong");
    }
  }

  return (
    <Controller
      name="memberToReview"
      control={control}
      rules={{ required: "Member to review is required" }}
      render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
        <MemberPicker
          onDismiss={onDismissModal}
          {...{ value, onBlur, visible, error }}
          onChange={(reviewer) => {
            onChange(reviewer);
            if (reviewer) Keyboard.dismiss();
          }}
          description="Select a new reviewer:"
          title="Send to Another Reviewer"
          placeholder="Member"
          testID="pick-reviewer"
          FooterComponent={
            <ActionsContainer
              errorAlert
              errorMessage={apiError}
              onCloseErrorMessage={setApiError}
              horizontalStyle="all"
              insideModal
            >
              <Button onPress={onDismissModal}>Cancel</Button>
              <Button
                loading={loading}
                disabled={isSubmitting}
                isValid={isValid}
                onPress={handleSubmit(onSubmit, onSubmitError)}
                mode="contained"
              >
                Send
              </Button>
            </ActionsContainer>
          }
        />
      )}
    />
  );
}
